import * as React from 'react';
import { tidyText } from '@cfl/assets-shared';

import { cn } from '@/lib/utils';

/**
 * What somebody typed, shown in a list that is meant to be scanned.
 *
 * Every ticket queue worth copying - Jira Service Management, Zendesk, ServiceNow -
 * puts a ONE-LINE subject in the row and keeps the body behind it. A queue is read by
 * running an eye down it looking for the one that matters, and that only works when
 * every row is the same height. This app has no subject field, deliberately: asking an
 * employee to write a title as well as a description is one more box between them and
 * telling IT their laptop is broken. So the first line acts as the subject, and the
 * rest is a click away.
 *
 * Unlike those tools, the click is an inline expansion rather than a detail screen.
 * The people who only READ this screen - HR, a VIEWER - have no Review button to open,
 * and a truncated report they cannot open at all is worse than no truncation.
 */
export function FreeText({
  text,
  prefix,
  accent = true,
  className,
}: {
  text: string;
  /** Rendered inline before the text, so it is part of the clamped line - IT's name
   *  on a note is read as the start of the sentence, not as a heading above it. */
  prefix?: React.ReactNode;
  /** The left rule. Off where the text already sits in a panel of its own. */
  accent?: boolean;
  className?: string;
}) {
  const [expanded, setExpanded] = React.useState(false);
  const [clamped, setClamped] = React.useState(false);
  const ref = React.useRef<HTMLParagraphElement>(null);
  const tidied = tidyText(text);

  // Measured, not guessed from the character count: the same sentence fits one line on
  // a desktop and three on a phone. Skipped while expanded, so `clamped` keeps the
  // answer from before the text was opened and "Show less" does not vanish.
  React.useLayoutEffect(() => {
    const element = ref.current;
    if (!element || expanded) return;

    const measure = () => setClamped(element.scrollHeight > element.clientHeight + 1);
    measure();

    const observer = new ResizeObserver(measure);
    observer.observe(element);
    return () => observer.disconnect();
  }, [tidied, expanded]);

  return (
    // The left rule marks this as the reporter's own words, the way the same text is
    // set in the emails - and without adding a third grey panel to a row that already
    // carries one for the handover condition and one for IT's note.
    <div
      className={cn(
        'flex flex-wrap items-baseline gap-x-2',
        accent && 'border-l-2 border-primary/30 pl-2.5',
      )}
    >
      <p
        ref={ref}
        className={cn('min-w-0 flex-1 whitespace-pre-wrap text-xs', !expanded && 'line-clamp-1', className)}
      >
        {prefix}
        {tidied}
      </p>

      {(clamped || expanded) && (
        <button
          type="button"
          onClick={() => setExpanded((open) => !open)}
          className="shrink-0 text-[11px] font-medium text-muted-foreground underline-offset-2 hover:underline"
        >
          {expanded ? 'Show less' : 'Show more'}
        </button>
      )}
    </div>
  );
}
